import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      panelTitle="Set New Password"
      panelText="Choose a strong password to secure your Somcore Hub account."
      eyebrow="Account Recovery"
      title="Set New Password"
      subtitle="Enter and confirm your new password below"
      footer={
        <div className="mt-6 text-center">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="back-to-login-link">
            <ArrowLeft className="h-4 w-4" /> Back to Sign In
          </Link>
        </div>
      }
    >
      {!token ? (
        <p className="mt-8 rounded-lg bg-[#F4E7E7] p-4 text-center text-sm text-[#9D4848]" data-testid="reset-invalid">
          This reset link is invalid. Please request a new one from the Forgot Password page.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
          <div className="space-y-2">
            <label htmlFor="password" className="flex items-center gap-1 text-xs font-semibold tracking-wide text-slate-600">
              <span className="text-primary">*</span> New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="New password" className="h-11 pl-10" data-testid="reset-password" />
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="confirm" className="flex items-center gap-1 text-xs font-semibold tracking-wide text-slate-600">
              <span className="text-primary">*</span> Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required placeholder="Confirm password" className="h-11 pl-10" data-testid="reset-confirm" />
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
          <Button type="submit" className="h-11 w-full text-base" disabled={loading} data-testid="reset-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update Password
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
